/**
 * Payment gateway switch.
 *
 * Exactly one gateway is active at a time, chosen by REACT_APP_PAYMENT_GATEWAY:
 *      REACT_APP_PAYMENT_GATEWAY = 0  ->  PayU      (default when unset)
 *      REACT_APP_PAYMENT_GATEWAY = 1  ->  Razorpay
 *
 * Keep this in sync with PAYMENT_GATEWAY in the backend .env. The backend also stamps the
 * gateway it actually used onto the create-order response, and that value wins -- so a
 * mismatched .env can never send the customer to the wrong checkout.
 */

export type PaymentGatewayName = 'payu' | 'razorpay';

const RAZORPAY_CHECKOUT_SRC = 'https://checkout.razorpay.com/v1/checkout.js';

const GATEWAY_BY_FLAG: Record<string, PaymentGatewayName> = {
    '0': 'payu',
    '1': 'razorpay'
};

export const getActiveGateway = (): PaymentGatewayName => {
    const flag = String(process.env.REACT_APP_PAYMENT_GATEWAY ?? '0').trim();
    return GATEWAY_BY_FLAG[flag] || 'payu';
};

/** The gateway the backend actually created the order with, falling back to the .env flag. */
export const resolveGateway = (responseGateway?: string): PaymentGatewayName => {
    if (responseGateway === 'razorpay' || responseGateway === 'payu') {
        return responseGateway;
    }
    return getActiveGateway();
};

export const isRazorpay = (responseGateway?: string): boolean => resolveGateway(responseGateway) === 'razorpay';

/** Injects Razorpay's checkout script once, and resolves when it is ready to use. */
export const loadRazorpayScript = (): Promise<boolean> => {
    return new Promise((resolve) => {
        if (typeof window === 'undefined') {
            return resolve(false);
        }

        if ((window as any).Razorpay) {
            return resolve(true);
        }

        const existing = document.querySelector<HTMLScriptElement>(`script[src="${RAZORPAY_CHECKOUT_SRC}"]`);

        if (existing) {
            existing.addEventListener('load', () => resolve(true));
            existing.addEventListener('error', () => resolve(false));
            return;
        }

        const script = document.createElement('script');
        script.src = RAZORPAY_CHECKOUT_SRC;
        script.async = true;
        script.onload = () => resolve(true);
        script.onerror = () => resolve(false);
        document.body.appendChild(script);
    });
};

/**
 * PayU is a plain form POST to the gateway -- unchanged from the original integration.
 */
export const submitPayuForm = (paymentURL: string, data: Record<string, unknown>) => {
    const form = document.createElement('form');
    form.method = 'post';
    form.action = paymentURL;
    Object.entries(data).forEach(([key, value]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = key;
        input.value = String(value);
        form.appendChild(input);
    });

    document.body.appendChild(form);
    form.submit();
};
